import { useEffect } from 'react'
import { useAuth } from '../auth/AuthContext'
import { api } from '../api/client'
import { identifyYandexUser, pushYandexEcommercePurchase } from '../analytics/yandexMetrika'
import {
  PAYMENT_ANALYTICS_OUTBOX_KEY,
  PAYMENT_ANALYTICS_OUTBOX_UPDATED_EVENT,
  readPaymentAnalyticsOutbox,
  removePaymentAnalytics,
  type PaymentAnalyticsRecord
} from '../billing/paymentAnalyticsOutbox'

const ECOM_SENT_KEY_PREFIX = 'billing.ym_ecom_purchase_sent.'
const PAYMENT_LOCK_PREFIX = 'fravai-payment-analytics-'
const MINOR_PER_UNIT = 100
const POLL_INTERVAL_MS = 5000
const MAX_CONCURRENT_PAYMENTS = 3

function toOrderPrice(amountMinor?: number): number | undefined {
  if (typeof amountMinor !== 'number' || !Number.isFinite(amountMinor) || amountMinor <= 0) return undefined
  return Number((amountMinor / MINOR_PER_UNIT).toFixed(2))
}

function wasSent(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

function markSent(key: string): boolean {
  try {
    window.localStorage.setItem(key, '1')
    return true
  } catch {
    return false
  }
}

async function withPaymentLock(paymentId: number, task: () => Promise<void>) {
  const lockManager = typeof navigator !== 'undefined' ? navigator.locks : undefined
  if (!lockManager) {
    await task()
    return
  }

  await lockManager.request(
    `${PAYMENT_LOCK_PREFIX}${paymentId}`,
    { ifAvailable: true },
    async (lock) => {
      if (lock) await task()
    }
  )
}

async function processPayment(payment: PaymentAnalyticsRecord) {
  await withPaymentLock(payment.paymentId, async () => {
    const ecomSentKey = `${ECOM_SENT_KEY_PREFIX}${payment.paymentId}`
    let ecomSent = wasSent(ecomSentKey)

    if (ecomSent) {
      removePaymentAnalytics(payment.paymentId)
      return
    }

    let status
    try {
      status = await api.getBillingPaymentStatus(payment.paymentId)
    } catch (error: any) {
      if (String(error?.message || '').startsWith('404:')) {
        removePaymentAnalytics(payment.paymentId)
      }
      return
    }

    const normalizedStatus = String(status.status || '').toUpperCase()
    if (normalizedStatus === 'CANCELED' || normalizedStatus === 'FAILED') {
      removePaymentAnalytics(payment.paymentId)
      return
    }
    if (normalizedStatus !== 'PAID') return

    const orderPrice = toOrderPrice(status.amountMinor) ?? toOrderPrice(payment.amountMinor)
    const currency = String(status.currency || payment.currency || 'RUB').toUpperCase()
    const orderId = status.providerPaymentId || String(status.paymentId)

    if (!ecomSent && typeof orderPrice === 'number') {
      const queued = pushYandexEcommercePurchase({
        orderId,
        revenue: orderPrice,
        currency,
        productId: payment.packCode?.trim() || 'balance_topup',
        productName: payment.packTitle?.trim() || 'Balance top-up',
        quantity: 1
      })
      if (queued && markSent(ecomSentKey)) {
        ecomSent = true
      }
    }

    if (ecomSent) {
      removePaymentAnalytics(payment.paymentId)
    }
  })
}

export default function BillingMetrikaTracker() {
  const { hydrated, isAuthed, profile } = useAuth()

  useEffect(() => {
    if (!hydrated || !isAuthed || !profile?.id) return
    identifyYandexUser(profile.id)
  }, [hydrated, isAuthed, profile?.id])

  useEffect(() => {
    if (!hydrated || !isAuthed) return

    let stopped = false
    let processing = false

    const processOutbox = async () => {
      if (stopped || processing || document.visibilityState === 'hidden') return
      const payments = readPaymentAnalyticsOutbox().slice(0, MAX_CONCURRENT_PAYMENTS)
      if (!payments.length) return

      processing = true
      try {
        await Promise.all(payments.map(processPayment))
      } finally {
        processing = false
      }
    }

    const onStorage = (event: StorageEvent) => {
      if (event.key === PAYMENT_ANALYTICS_OUTBOX_KEY) {
        void processOutbox()
      }
    }
    const onOutboxUpdated = () => { void processOutbox() }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void processOutbox()
    }

    window.addEventListener('storage', onStorage)
    window.addEventListener(PAYMENT_ANALYTICS_OUTBOX_UPDATED_EVENT, onOutboxUpdated)
    document.addEventListener('visibilitychange', onVisibilityChange)
    const timer = window.setInterval(() => { void processOutbox() }, POLL_INTERVAL_MS)
    void processOutbox()

    return () => {
      stopped = true
      window.clearInterval(timer)
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(PAYMENT_ANALYTICS_OUTBOX_UPDATED_EVENT, onOutboxUpdated)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [hydrated, isAuthed])

  return null
}
